
export default function PainPoints() {
  const painPoints = [
    {
      icon: 'ri-layout-masonry-line',
      title: 'Fragmented Experience',
      description: 'Users forced to navigate between 5-7 different tools for payments, communications, maintenance, and documents.',
      severity: 'Critical',
      color: '#EF4444'
    },
    {
      icon: 'ri-time-line',
      title: 'Slow Response Times',
      description: 'Average maintenance request takes 7-14 days due to manual routing and lack of automated workflows.',
      severity: 'High',
      color: '#F97316'
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Zero Transparency',
      description: 'Residents have no visibility into where their dues go, status of requests, or upcoming community decisions.',
      severity: 'Critical',
      color: '#EF4444'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Poor Mobile Experience',
      description: 'Existing solutions are desktop-first with clunky mobile apps that frustrate on-the-go users.',
      severity: 'High',
      color: '#F97316'
    },
    {
      icon: 'ri-file-paper-2-line',
      title: 'Paper-Heavy Processes',
      description: 'Board meetings, voting, and compliance still rely on physical documents and in-person signatures.',
      severity: 'Medium',
      color: '#F97316'
    },
    {
      icon: 'ri-chat-off-line',
      title: 'Communication Gaps',
      description: 'Important announcements get lost in email, and there\'s no centralized community communication hub.',
      severity: 'High',
      color: '#F97316'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#EF4444]/10 text-[#EF4444] rounded-full text-sm font-semibold mb-4 border border-[#EF4444]/20">
            04 — Problems
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            Core UX Pain Points
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            Analyzing the critical failures in existing HOA management applications that create friction for all user types.
          </p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {painPoints.map((point, index) => (
            <div 
              key={index}
              className="bg-[#FAFBFC] rounded-2xl p-6 border border-[#E2E8F0] hover:border-[#EF4444]/30 hover:shadow-lg transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div 
                  className="w-12 h-12 flex items-center justify-center rounded-xl border"
                  style={{ backgroundColor: `${point.color}10`, borderColor: `${point.color}20` }}
                >
                  <i className={`${point.icon} text-xl`} style={{ color: point.color }}></i>
                </div>
                <span 
                  className="px-3 py-1 rounded-full text-xs font-semibold border"
                  style={{ 
                    backgroundColor: `${point.color}10`,
                    color: point.color,
                    borderColor: `${point.color}20`
                  }}
                >
                  {point.severity}
                </span>
              </div>
              <h3 className="text-xl font-bold text-[#1E293B] mb-3">{point.title}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{point.description}</p>
            </div>
          ))}
        </div>

        {/* Competitor Analysis */}
        <div className="bg-[#FAFBFC] rounded-3xl p-8 border border-[#E2E8F0] shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">Competitor Landscape Analysis</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-4 px-4 text-[#64748B] font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-[#64748B] font-medium">Competitor A</th>
                  <th className="text-center py-4 px-4 text-[#64748B] font-medium">Competitor B</th>
                  <th className="text-center py-4 px-4 text-[#64748B] font-medium">Competitor C</th>
                  <th className="text-center py-4 px-4 text-[#1FA372] font-bold">Our Solution</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#F1F5F9]">
                  <td className="py-4 px-4 text-[#1E293B] font-medium">Mobile-First Design</td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-subtract-line text-[#F97316] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                </tr>
                <tr className="border-b border-[#F1F5F9]">
                  <td className="py-4 px-4 text-[#1E293B] font-medium">Real-time Notifications</td>
                  <td className="py-4 px-4 text-center"><i className="ri-subtract-line text-[#F97316] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                </tr>
                <tr className="border-b border-[#F1F5F9]">
                  <td className="py-4 px-4 text-[#1E293B] font-medium">Integrated Payments</td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-subtract-line text-[#F97316] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                </tr>
                <tr className="border-b border-[#F1F5F9]">
                  <td className="py-4 px-4 text-[#1E293B] font-medium">Multi-language Support</td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-[#1E293B] font-medium">AI-Powered Insights</td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-close-line text-[#EF4444] text-xl"></i></td>
                  <td className="py-4 px-4 text-center"><i className="ri-check-line text-[#1FA372] text-xl"></i></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-center gap-8 mt-8 pt-6 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#1FA372]"></i>
              <span className="text-[#64748B] text-sm">Full Support</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-subtract-line text-[#F97316]"></i>
              <span className="text-[#64748B] text-sm">Partial</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-close-line text-[#EF4444]"></i>
              <span className="text-[#64748B] text-sm">Not Available</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
